import { Dispatch, SetStateAction } from "react";
import { t } from "i18next";

import { ApiResponse } from "../../../../../../@api";

import { translationKeys } from "../../../../../models/app-translation-keys";
import {
  ISharedDialogAction,
  ISharedDialogActionContext,
  sharedDialog,
} from "../../../../Shared/Components/Dialogs/SharedDialog";
import { IManageTermsOfUseDialogState } from "./ManageTermsOfUseDialog";

interface IManageTermsOfUseDialogFooterActionsProps {
  onHide: (success: boolean) => void;
  uploadTerms: (file: File) => Promise<ApiResponse<string>>;
}

// state update function for uploading status to avoid repetition in onClick handler
const setUpdating = (setState: Dispatch<SetStateAction<IManageTermsOfUseDialogState>>, value: boolean) => {
  setState(prev => ({
    ...prev,
    isUpdating: value,
  }));
};

const getFormStatus = (state: IManageTermsOfUseDialogState) => {
  const isDisabled = !state.termsFile || state.isUpdating;
  const tooltip = state.isUpdating ? t(featureTranslationKeys.upload.inProgress) : undefined;

  return {
    isDisabled,
    tooltip,
  };
};

const featureTranslationKeys = translationKeys.adminArea.termsOfUse.actions;

export const manageTermsOfUseDialogFooterActions = ({
  onHide,
  uploadTerms,
}: IManageTermsOfUseDialogFooterActionsProps) => {
  return ({ id, sharedState, setSharedDialogState }: ISharedDialogActionContext): ISharedDialogAction[] => {
    const state = sharedState as IManageTermsOfUseDialogState;
    const setState = setSharedDialogState as Dispatch<SetStateAction<IManageTermsOfUseDialogState>>;
    const formStatus = getFormStatus(state);

    const handleUpload = async () => {
      if (!state.termsFile || state.isUpdating) {
        return;
      }

      setUpdating(setState, true);

      try {
        const response = await uploadTerms(state.termsFile.file);

        sharedDialog.closeSharedDialog(id);
        onHide(Boolean(response?.raw?.ok));
      } finally {
        setUpdating(setState, false);
      }
    };

    return [
      {
        label: t(translationKeys.actions.cancel),
        isTextButton: true,
        isDisabled: false,
        isLoading: false,
        tooltip: undefined,
        tooltipOptions: undefined,
        onClick: () => {
          sharedDialog.closeSharedDialog(id);
          onHide(false);
        },
      },

      {
        label: t(featureTranslationKeys.upload.name),
        isTextButton: false,
        isLoading: state.isUpdating,
        isDisabled: formStatus.isDisabled,
        tooltip: formStatus.tooltip,
        tooltipOptions: {
          position: "top",
          showOnDisabled: true,
          showDelay: 200,
        },
        onClick: handleUpload,
      },
    ];
  };
};
